
import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios';
import { Link } from 'react-router-dom';
import {baseUrl, limit, formatDate, formatMasterReportValue} from "../../comman/config";
import Loader from '../../components/Loader'
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/solid";
import * as XLSX from 'xlsx';
import { DateSessionReportFilter } from "../../components/Filter/DateSessionReportFilter";
import { isFilterActive, matchFilterText, patchFilter } from "../../components/Filter/columnFilter";
import ColumnFilterRow from "../../components/Filter/ColumnFilterRow";
import { ItemName, itemDisplayName, itemSearchText, itemExportName } from "../../components/ItemName";
import { useIsMobile } from "../../comman/useIsMobile";

// Grouped-header labels for each column section ('' = no group header, e.g. frozen id
// columns and the standalone Method column).
const SECTION_LABELS = {
    id: '',
    planned: 'Planned',
    method: '',
    production: 'Production',
    historical: 'Historical',
    bowls: 'Bowls',
    dish: 'Dish issued',
    physical: 'Excess / Shortage Qty.',
    net: 'NET Consumption',
    density: 'Density',
    remarks: '',
};

// Thick dark divider between sections (survives fractional browser zoom, mirrors the
// Production Estimation .pe-divider pattern).
const SECTION_DIVIDER = '3px solid #111827';

// Remarks are free text and can run long. The cell shows a 20-character prefix on ONE line, the
// same as the Excess Report -- a remark must never grow the row it sits in, or the whole table
// reads as ragged. The full text is the cell's tooltip, and clicking opens the popup. Exports are
// unaffected: they write the remark in full (see the Remarks column's filterText).
const REMARKS_CHAR_LIMIT = 20;
const truncate = (text) => {
    const full = String(text ?? '');
    return full.length > REMARKS_CHAR_LIMIT ? `${full.slice(0, REMARKS_CHAR_LIMIT)}...` : full;
};

function List() {

    const today = new Date().toISOString().split("T")[0];
    const [filteredList, setFilteredList] = useState([]);
    const [reportList, setReportList] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItem, setTotalItem] = useState(0);
    const [popupOpen, setPopupOpen] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [session, setSession] = useState("");
    const [item, setItem] = useState("");
    const [method, setMethod] = useState("");
    // Events picked in the filter's multi-select. Sent to the API so the report covers those
    // events only -- the filter carries one from/to pair, so several events span the gaps between
    // them and those in-between dates would otherwise be reported on. [] = no narrowing.
    const [events, setEvents] = useState([]);
    const [summary, setSummary] = useState(true);
    const [reportType, setReportType] = useState(1);
    const [loading, setLoading] = useState(false)
    // Raw Report "tick" column is visible to super admin only. Superadmin is role 0 -- role 1 is
    // the ordinary admin, so comparing against 1 showed the column to admins and hid it from the
    // one user meant to have it.
    const userRole = localStorage.getItem('userRole');
    const isSuperAdmin = Number(userRole) === 0;
    // The freeze-pane is desktop only. Select + Date + Session + Menu Item are 398px of frozen
    // width, which on a phone is the entire viewport -- the measures were pinned off-screen with no
    // way to scroll to them. Below `sm` the table scrolls as one instead.
    const isMobile = useIsMobile();

    // "Show Columns" state: set of hidden column keys (persisted across visits).
    const [hiddenCols, setHiddenCols] = useState(() => {
        try {
            const saved = localStorage.getItem('rawReportHiddenCols');
            return new Set(saved ? JSON.parse(saved) : []);
        } catch (e) { return new Set(); }
    });
    const [showColsOpen, setShowColsOpen] = useState(false);
    // Which sections are expanded in the Show Columns dropdown. Collapsed by default -- this report
    // has ten sections and forty-odd columns, so opening them all at once buries the list.
    const [openSections, setOpenSections] = useState(() => new Set());
    const toggleOpenSection = (key) => setOpenSections(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });
    const showColsRef = useRef(null);
    // Row whose Remarks popup is open, and the textarea's working copy.
    const [remarksRow, setRemarksRow] = useState(null);
    const [remarksText, setRemarksText] = useState('');

    // Inline per-column filters -- see components/Filter/columnFilter for the operator set.
    const [filters, setFilters] = useState({});
    const setColFilter = (key, patch) => setFilters(prev => patchFilter(prev, key, patch));

    useEffect(() => {
        try { localStorage.setItem('rawReportHiddenCols', JSON.stringify([...hiddenCols])); } catch (e) {}
    }, [hiddenCols]);

    // Close the Show Columns dropdown on outside click.
    useEffect(() => {
        const onDocClick = (e) => {
            if (showColsRef.current && !showColsRef.current.contains(e.target)) setShowColsOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    // Remarks (+) shows on rows the Method column reports as Kgs or Readymade-Nos. That covers
    // genuine Kgs (measurements 1) AND "Converted-Kgs" -- the generated mirror rows, which carry
    // methodName.id 1 while keeping the source measurements (2 for ltr, 3 for nos). A litres or
    // nos SOURCE row is not eligible, so exactly one row per item can hold a remark and the
    // date+session+menu_item_id key never collides.
    const remarksEligible = (row) => [1, 6].includes(Number(row?.methodName?.id));

    const openRemarks = (row) => {
        setRemarksRow(row);
        setRemarksText(row.raw_report_remarks ?? '');
    };

    const saveRemarks = async () => {
        if (!remarksRow) return;
        const text = remarksText.trim();
        const { date, session, menu_item_id } = remarksRow;
        try {
            await axios.post(`${baseUrl}/reports/raw_report_remarks`, { date, session, menu_item_id, remarks: text });
            // Update every row for this item -- a source row and its Kgs mirror share the key, so
            // both must reflect the saved remark without a refetch.
            const stored = text === '' ? null : text;
            const sameRow = (r) => r.date === date && r.session === session && r.menu_item_id === menu_item_id;
            setReportList(prev => prev.map(r => (sameRow(r) ? { ...r, raw_report_remarks: stored } : r)));
            setFilteredList(prev => prev.map(r => (sameRow(r) ? { ...r, raw_report_remarks: stored } : r)));
            setRemarksRow(null);
        } catch (e) {
            console.error('Failed to save remarks', e);
        }
    };

    // Persist a super-admin tick (optimistic local update; revert on failure).
    const handleToggleTick = async (index, checked) => {
        const row = filteredList[index];
        if (!row) return;
        setFilteredList(prev => prev.map((r, i) => i === index ? { ...r, is_ticked: checked } : r));
        try {
            await axios.post(`${baseUrl}/reports/raw_report_selection`, {
                date: row.date, session: row.session, menu_item_id: row.menu_item_id, is_ticked: checked,
                role: userRole,
            });
        } catch (e) {
            console.error('Failed to save selection', e);
            setFilteredList(prev => prev.map((r, i) => i === index ? { ...r, is_ticked: !checked } : r));
        }
    };

    useEffect(() => {

            getShortageList();

    }, [fromDate,toDate, session, item, method]);


    // Match the DISPLAYED method (methodName.id), not the stored unit (measurements). A ltr -> Kgs
    // or nos -> Kgs mirror row shows "Kgs" in the Method column but keeps the source measurements
    // (2 or 3), so filtering on measurements hid exactly the converted rows that read as Kgs --
    // and let a litres row through when Kgs was selected. Selecting "Kgs" now returns every row
    // the report presents as Kgs, converted ones included.
    // Same rule the Master Report and Consumption Report already use.
    useEffect(() => {
        let filterelist;
        if(method && method.length > 0){
            const methodIds = method.map(m => Number(m.value));
            filterelist = reportList.filter((item) => item.measurements
                && methodIds.includes(Number(item.methodName?.id ?? item.measurements)));
        } else{
            filterelist = reportList.filter((item) => item.measurements);
        }
        setFilteredList(filterelist);
    }, [ method, reportList]);
    // Args default to the current state, so the useEffect above can call this with none. Submit
    // passes the filter's values explicitly -- state is set in the same tick and would still be
    // stale here, so reading state would fetch the previous filter's data.
    const getShortageList = async (f = fromDate, t = toDate, s = session, i = item, m = method, ev = events) => {

        setPopupOpen(true);
        setLoading(true)
        const response = await axios.get(`${baseUrl}/reports/master_report?page=${page}&limit=${limit}&fromDate=${f}&toDate=${t}&session=${encodeURIComponent(JSON.stringify(s))}&item=${encodeURIComponent(JSON.stringify(i))}&method=${encodeURIComponent(JSON.stringify(m))}&event=${encodeURIComponent(JSON.stringify(ev ?? []))}&itemFilter=in`, {

        });

        setLoading(false)
        setPopupOpen(false);
        setReportList(response.data.data);
        setFilteredList(response.data.data);
        setTotalItem(response.data.totalItem);
    }

    const handelFilterChange= (fromDate, toDate, session, item, method, pickedEvents) => {
        console.log(item);
        setFromDate(fromDate);
        setToDate(toDate);
        setSession(session);
        setItem(item);
        setMethod(method);
        setEvents(pickedEvents ?? []);


    };

    // const hadelSummary = (summary) => {
    //     setSummary(summary);
    // }

    const handelReportChange = (data) => {
        console.log(data);
        setReportType(data.value);
    }

    // Excel export. Built from the same column model the table renders, so the workbook carries
    // exactly the screen's section headings and column labels -- the two used to be maintained as
    // separate hand-written lists and drifted apart every time a column was renamed.
    //
    // Every section and column is exported, including ones hidden through Show Columns: the file is
    // the full record, the screen is the working view. The Select checkbox has no value to export.
    const handleExport = () => {
        const exportCols = buildColumns().filter(c => c.key !== 'select');

        // Row 1: section headings, merged across the columns they cover -- the grouped header row.
        // Row 2: the column labels themselves.
        const sectionRow = [];
        const merges = [];
        for (let i = 0; i < exportCols.length; ) {
            const section = exportCols[i].section;
            let j = i;
            while (j + 1 < exportCols.length && exportCols[j + 1].section === section) j++;
            sectionRow.push(SECTION_LABELS[section] ?? '');
            for (let k = i + 1; k <= j; k++) sectionRow.push('');
            if (j > i) merges.push({ s: { r: 0, c: i }, e: { r: 0, c: j } });
            i = j + 1;
        }
        const labelRow = exportCols.map(c => c.label);

        // filterText where a column renders JSX (Menu Item's (+) button, Remarks' clickable text);
        // everything else renders a plain value already. exportText where the file wants something
        // other than what the filter matches -- Menu Item searches on both names but writes the
        // Gujarati one with the English beside it, rather than the two run together.
        const cellText = (c, item) => {
            const accessor = c.exportText ?? c.filterText;
            const raw = accessor ? accessor(item) : c.render(item);
            return raw == null ? '' : raw;
        };
        const data = filteredList.map(item => exportCols.map(c => cellText(c, item)));

        const worksheet = XLSX.utils.aoa_to_sheet([sectionRow, labelRow, ...data]);
        worksheet['!merges'] = merges;
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Raw Report");

        XLSX.writeFile(workbook, `Raw_Report_${fromDate ? '_' + formatDate(fromDate) : ''}to${toDate ? '_' + formatDate(toDate) : ''}${session?.label ? '_' + session.label : ''}.xlsx`);
    }

    // ---- Column model: single source of truth for Show Columns, freeze-pane and section dividers ----
    // Each column: { key, section, label, frozen?, width?, bg?, render(item, index) }.
    // Report-type visibility (t1/n2/n3) is applied here so hidden-by-report columns simply aren't built.
    const buildColumns = () => {
        const fmt = formatMasterReportValue;
        const t1 = reportType == 1, n2 = reportType != 2, n3 = reportType != 3;
        const cols = [];
        // Frozen id columns (freeze-pane up to Menu Item). width drives the sticky left offset.
        if (isSuperAdmin) cols.push({ key: 'select', section: 'id', label: 'Select', frozen: true, width: 56,
            render: (item, index) => item.is_selectable ? <input type="checkbox" checked={!!item.is_ticked} onChange={(e) => handleToggleTick(index, e.target.checked)} /> : null });
        cols.push({ key: 'date', section: 'id', label: 'Date', frozen: true, width: 96, render: (item) => formatDate(item.date) });
        cols.push({ key: 'session', section: 'id', label: 'Session', frozen: true, width: 96, render: (item) => item.session_name });
        // (+) opens the Remarks popup. Super admin only -- writing a remark is their call, the same
        // way the Select tick is. Everyone else still reads remarks: the text stays visible in the
        // Remarks column and clicking it opens the popup read-only.
        // Beyond the role, only rows measured in Kgs, Converted-Kgs or Readymade-Nos qualify -- see
        // remarksEligible. An existing remark underlines the name.
        cols.push({ key: 'menu_item', section: 'id', label: 'Menu Item', frozen: true, width: 150,
            filterText: itemSearchText, exportText: itemExportName, render: (item) => (
            <span className="inline-flex items-center gap-1">
                <span className={item.raw_report_remarks ? 'underline decoration-2' : undefined}
                    title={item.raw_report_remarks || undefined}><ItemName item={item} /></span>
                {isSuperAdmin && remarksEligible(item) && (
                    <button type="button" onClick={() => openRemarks(item)}
                        title={item.raw_report_remarks ? 'Edit remarks' : 'Add remarks'}
                        className="shrink-0 px-1 leading-none text-blue-700 border border-blue-400 rounded hover:bg-blue-50">+</button>
                )}
            </span>
        ) });
        // Planned
        cols.push({ key: 'planned_count', section: 'planned', label: 'Count', bg: '#8ed873', render: (item) => fmt(item.raw_material?.estimated_count, 'estimated_count') });
        if (n2) cols.push({ key: 'planned_ras', section: 'planned', label: 'RAS', render: (item) => fmt(item.raw_material.ras_qty_per_100_person, 'ras_qty_per_100_person') });
        cols.push({ key: 'planned_rm', section: 'planned', label: 'RM', render: (item) => fmt(item.raw_material.planned_rm_qty, 'planned_rm_qty') });
        if (t1) cols.push({ key: 'planned_fg', section: 'planned', label: 'FG', render: (item) => fmt(item.planned_fg ?? 0, 'planned_fg') });
        if (t1) cols.push({ key: 'planned_rm_to_fg', section: 'planned', label: 'RM to FG Ratio', render: (item) => fmt(item.planned_rm_to_fg_ratio ?? 0, 'planned_rm_to_fg_ratio') });
        if (t1) cols.push({ key: 'planned_fg_person', section: 'planned', label: 'FG / Person', render: (item) => fmt(item.planned_fg_per_person ?? 0, 'planned_fg_per_person') });
        // Method (standalone column, own empty group header)
        if (n3) cols.push({ key: 'method', section: 'method', label: 'Method', render: (item) => item.methodName?.name ?? '-' });
        // Production
        if (n3) cols.push({ key: 'prod_batch', section: 'production', label: 'Batch', render: (item) => fmt(item.batch, 'batch') });
        cols.push({ key: 'prod_actual_rm', section: 'production', label: 'Actual RM', render: (item) => fmt(item.batch_raw_material_qty, 'batch_raw_material_qty') });
        if (n2) cols.push({ key: 'prod_ras', section: 'production', label: 'RAS', render: (item) => fmt(item.production_ras ?? 0, 'production_ras') });
        cols.push({ key: 'prod_fg', section: 'production', label: 'FG', render: (item) => fmt(item.finishedQty ?? 0, 'finishedQty') });
        if (n3) cols.push({ key: 'prod_rm_to_fg', section: 'production', label: 'RM to FG Ratio', render: (item) => fmt(item.current_rm_to_fg_ratio ?? 0, 'current_rm_to_fg_ratio') });
        if (t1) cols.push({ key: 'prod_fg_person', section: 'production', label: 'FG / Person', render: (item) => fmt(item.material_fg_per_person ?? 0, 'material_fg_per_person') });
        if (t1) cols.push({ key: 'prod_count', section: 'production', label: 'Count', bg: '#8ed873', render: (item) => fmt(item.production_count ?? 0, 'production_count') });
        // Historical
        if (t1) cols.push({ key: 'hist_fg_person', section: 'historical', label: 'FG / Person', render: (item) => fmt(item.fg_per_person_gms ?? 0, 'fg_per_person_gms') });
        if (n3) cols.push({ key: 'hist_rm_to_fg', section: 'historical', label: 'RM to FG Ratio', render: (item) => fmt(item.rm_to_fg_ratio ?? 0, 'rm_to_fg_ratio') });
        cols.push({ key: 'hist_count', section: 'historical', label: 'Count', bg: '#8ed873', render: (item) => fmt(item.food_qty ?? 0, 'food_qty') });
        // Bowls
        if (n3) {
            cols.push({ key: 'bowls_actual_qty', section: 'bowls', label: 'Actual 1 Bowl qty', render: (item) => fmt(item.no_of_rotis_in_1_bowl_icebox ?? 0, 'no_of_rotis_in_1_bowl_icebox') });
            cols.push({ key: 'bowls_initial', section: 'bowls', label: 'Total Bowls', render: (item) => fmt(item.no_of_actual_bowls, 'no_of_actual_bowls') });
            cols.push({ key: 'bowls_rm_ratio', section: 'bowls', label: 'Current RM to Bowl Ratio', render: (item) => fmt(item.current_rm_to_bowl_ratio, 'current_rm_to_bowl_ratio') });
            cols.push({ key: 'bowls_persons', section: 'bowls', label: 'Persons Per bowl', render: (item) => fmt(item.person_per_bowl ?? 0, 'person_per_bowl') });
        }
        // Dish issued
        if (n2) {
            cols.push({ key: 'dish_pass', section: 'dish', label: 'Pass Issued', render: (item) => fmt(item.pass_issued ?? 0, 'pass_issued') });
            cols.push({ key: 'dish_api', section: 'dish', label: 'Dish issued as per API', render: (item) => fmt(item.dish_issue_count ?? 0, 'dish_issue_count') });
            cols.push({ key: 'dish_final', section: 'dish', label: 'Final Dish count', render: (item) => fmt(item.final_dish_count ?? 0, 'final_dish_count') });
        }
        // Excess / Shortage Qty. -- the same shortage told three ways, each measure following its
        // own pair: Actual in Kgs, then Persons, then nos. (bowls), and vs. Planned likewise. The
        // two nos. columns used to sit apart in an "Excess / Shortage Bowls" section of their own,
        // which put the bowl count a dozen columns away from the kgs and persons it restates.
        // They stay unconditional, as they were in that section, so they show on every report type.
        if (n2) cols.push({ key: 'phys_fg_qty', section: 'physical', label: 'Actual Fg Qty (Kgs)', render: (item) => fmt(item.excess_fg_qty ?? 0, 'excess_fg_qty') });
        if (t1) cols.push({ key: 'phys_persons', section: 'physical', label: 'Actual (Persons)', render: (item) => fmt(item.excess_fg_persons ?? 0, 'excess_fg_persons') });
        cols.push({ key: 'excess_bowls_phys', section: 'physical', label: 'Actual Bowl (Nos.)', render: (item) => fmt(item.excess_bowls_physical ?? 0, 'excess_bowls_physical') });
        if (t1) {
            cols.push({ key: 'phys_net_kgs', section: 'physical', label: 'vs. Planned FG Qty. (Kgs)', render: (item) => fmt(item.net_excess_fg_kgs ?? 0, 'net_excess_fg_kgs') });
            cols.push({ key: 'phys_net_persons', section: 'physical', label: 'vs. Planned (Persons)', render: (item) => fmt(item.net_excess_fg_persons ?? 0, 'net_excess_fg_persons') });
        }
        cols.push({ key: 'excess_bowls_net', section: 'physical', label: 'vs. Planned Bowl (Nos.)', render: (item) => fmt(item.excess_bowls_net ?? 0, 'excess_bowls_net') });
        // NET Consumption
        if (n2) {
            cols.push({ key: 'net_fg_person', section: 'net', label: 'Actual FG/person', render: (item) => fmt(item.fg_consumption_per_person ?? 0, 'fg_consumption_per_person') });
            cols.push({ key: 'net_ras', section: 'net', label: 'Actual RAS', render: (item) => fmt(item.raw_mat_per_100_person ?? 0, 'raw_mat_per_100_person') });
            cols.push({ key: 'net_avg_bowl', section: 'net', label: 'Actual Avg persons per bowl', render: (item) => fmt(item.avg_person_per_bowl ?? 0, 'avg_person_per_bowl') });
        }
        // Density
        cols.push({ key: 'density_std', section: 'density', label: 'Std (Consumption Master)', render: (item) => fmt(item.consumption_master?.avg_density ?? 0, 'std_density') });
        cols.push({ key: 'density_live', section: 'density', label: 'Live (Meal Master)', render: (item) => parseFloat(item.avg_density) > 0 ? fmt(item.avg_density, 'density') : '' });
        // Remarks last, after every measured column. filterText is the WHOLE remark, so the filter
        // searches all of it and the export writes all of it -- only the cell is shortened.
        // Clicking a remark opens the same popup the (+) uses, so the full text is readable (and
        // editable) however long it runs.
        cols.push({ key: 'remarks', section: 'remarks', label: 'Remarks', filterText: (item) => item.raw_report_remarks ?? '', render: (item) => {
            const full = item.raw_report_remarks ?? '';
            if (!full) return '';
            return (
                // One line, never wrapped: a remark of any length leaves the row at its own height.
                <span onClick={() => openRemarks(item)}
                    title={full}
                    className="cursor-pointer hover:underline inline-block text-left whitespace-nowrap">
                    {truncate(full)}
                </span>
            );
        } });
        return cols;
    };

    const columns = buildColumns();
    // Cumulative left offset for each frozen (freeze-pane) column.
    let _left = 0;
    columns.forEach(c => { if (c.frozen) { c.left = _left; _left += c.width; } });

    // Frozen id columns are always shown; everything else is toggleable via Show Columns.
    const toggleableColumns = columns.filter(c => c.section !== 'id');
    const allShown = toggleableColumns.every(c => !hiddenCols.has(c.key));
    const visibleColumns = columns.filter(c => c.section === 'id' || !hiddenCols.has(c.key));

    // Group consecutive visible columns by section for the grouped (top) header row.
    const headerGroups = [];
    visibleColumns.forEach(c => {
        const last = headerGroups[headerGroups.length - 1];
        if (last && last.section === c.section) last.cols.push(c);
        else headerGroups.push({ section: c.section, cols: [c] });
    });
    // Draw a thick divider before the first column of every section except the first group.
    const dividerKeys = new Set(headerGroups.slice(1).map(g => g.cols[0].key));

    // Show Columns is a two-level tree: each section, then its own columns underneath. Sections are
    // taken from the column model rather than listed separately, so a column added to buildColumns
    // appears in the dropdown without anyone remembering to register it. Sections with no label of
    // their own (Method, Remarks) have nothing to group, so their columns sit at the top level.
    const toggleableSections = [];
    toggleableColumns.forEach(c => {
        const last = toggleableSections[toggleableSections.length - 1];
        if (last && last.section === c.section) last.cols.push(c);
        else toggleableSections.push({ section: c.section, cols: [c] });
    });
    // A section ticks when every column under it is shown, and sits indeterminate while only some
    // are -- which is the state you land in after unticking one column of a ticked section.
    const sectionState = (grp) => {
        const shown = grp.cols.filter(c => !hiddenCols.has(c.key)).length;
        return { all: shown === grp.cols.length, some: shown > 0 && shown < grp.cols.length };
    };
    const toggleSection = (grp) => setHiddenCols(prev => {
        const next = new Set(prev);
        const { all } = sectionState(grp);
        grp.cols.forEach(c => (all ? next.add(c.key) : next.delete(c.key)));
        return next;
    });

    const toggleCol = (key) => setHiddenCols(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const toggleAll = (checked) => setHiddenCols(prev => {
        const next = new Set(prev);
        toggleableColumns.forEach(c => { if (checked) next.delete(c.key); else next.add(c.key); });
        return next;
    });

    // Frozen cell width kept fixed so the sticky left offsets line up exactly.
    const frozenSize = (c) => ({ width: c.width, minWidth: c.width, maxWidth: c.width });
    // Frozen positioning for a cell, or nothing at all on a phone.
    const freezeStyle = (c, zIndex, extra = {}) =>
        (c.frozen && !isMobile)
            ? { position: 'sticky', left: c.left, zIndex, ...frozenSize(c), ...extra }
            : {};

    // ---- Inline filter matching (contains / = / <= / >= / between / not between) ----
    // Columns whose render() returns JSX (Menu Item's (+) button, Remarks' clickable text) must
    // supply filterText -- String(<span>) is "[object Object]" and matches nothing.
    const matchFilter = (c, item) =>
        matchFilterText(c.filterText ? c.filterText(item) : c.render(item), filters[c.key]);
    const activeFilterCols = visibleColumns.filter(c => c.key !== 'select' && isFilterActive(filters[c.key]));
    const hasFilters = activeFilterCols.length > 0;
    const rowPasses = (item) => activeFilterCols.every(c => matchFilter(c, item));
    // Keep the ORIGINAL filteredList index so the super-admin tick checkbox toggles the right row.
    const visibleRows = filteredList
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => rowPasses(item));
    const clearFilters = () => setFilters({});

    return (
        <div className="p-4">
            {loading && <Loader/>}
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-2xl font-bold">Raw Report</h1>
                <div className="flex items-center">
                    {/* Show Columns: per-column visibility with an "All" master toggle. */}
                    <div className="relative inline-block mr-4" ref={showColsRef}>
                        <button
                            onClick={() => setShowColsOpen(o => !o)}
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring focus:ring-gray-200">
                            Show Columns ▾
                        </button>
                        {showColsOpen && (
                            <div className="absolute right-0 mt-2 w-72 max-h-96 overflow-auto bg-white border border-gray-300 rounded-md shadow-lg z-50 p-2 text-left">
                                <label className="flex items-center gap-2 px-2 py-1 font-semibold border-b border-gray-200 cursor-pointer">
                                    <input type="checkbox" checked={allShown} onChange={(e) => toggleAll(e.target.checked)} />
                                    <span>All</span>
                                </label>
                                {toggleableSections.map((grp, gi) => {
                                    const { all, some } = sectionState(grp);
                                    // No section label (Method, Remarks) -- nothing to group, so the
                                    // columns stand on their own rather than under an empty heading.
                                    if (!SECTION_LABELS[grp.section]) {
                                        return grp.cols.map(c => (
                                            <label key={c.key} className="flex items-center gap-2 px-2 py-1 font-semibold hover:bg-gray-50 cursor-pointer">
                                                <input type="checkbox" checked={!hiddenCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                                                <span>{c.label}</span>
                                            </label>
                                        ));
                                    }
                                    return (
                                        <div key={`${grp.section}-${gi}`} className="py-1 border-b border-gray-100 last:border-b-0">
                                            <div className="flex items-center gap-2 px-2 py-1 font-semibold hover:bg-gray-50">
                                                <input
                                                    type="checkbox"
                                                    checked={all}
                                                    // indeterminate is a DOM property React cannot set
                                                    // through JSX, so it goes on via the ref.
                                                    ref={(el) => { if (el) el.indeterminate = some; }}
                                                    onChange={() => toggleSection(grp)} />
                                                {/* The name expands the section; only the box changes
                                                    what the table shows. */}
                                                <button type="button" onClick={() => toggleOpenSection(grp.section)}
                                                    className="flex-1 flex items-center justify-between text-left cursor-pointer">
                                                    <span>{SECTION_LABELS[grp.section]}</span>
                                                    {openSections.has(grp.section)
                                                        ? <ChevronUpIcon className="w-4 h-4 shrink-0 text-gray-500" />
                                                        : <ChevronDownIcon className="w-4 h-4 shrink-0 text-gray-500" />}
                                                </button>
                                            </div>
                                            {openSections.has(grp.section) && grp.cols.map(c => (
                                                <label key={c.key} className="flex items-center gap-2 pl-8 pr-2 py-0.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                                    <input type="checkbox" checked={!hiddenCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                                                    <span>{c.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {hasFilters && (
                        <button
                            onClick={clearFilters}
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md mr-4 hover:bg-gray-300 focus:outline-none">
                            Clear Filters
                        </button>
                    )}
                    <Link to="/">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Back</button>
                    </Link>
                </div>
            </div>
            <div className="relative shadow-md sm:rounded-lg">
            <DateSessionReportFilter
            handleFilterChange={handelFilterChange}
            // hadelSummary={hadelSummary}
            handleExport={handleExport}
            handelReportChange={handelReportChange}
            handleSubmitFilters={getShortageList}
            handleClearFilters={clearFilters}
            multiEvent
            syncToDate
            />


                <div className="overflow-auto max-h-[75vh]">
                <table style={{ borderCollapse: 'separate', borderSpacing: 0, border: SECTION_DIVIDER }} className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 sticky top-0 z-20">
                        {/* Grouped section header row */}
                        <tr>
                            {headerGroups.map((g, gi) => {
                                if (g.section === 'id') {
                                    // Merged frozen header cell over the id columns (Select/Date/Session/Menu Item).
                                    return (
                                        <th key="grp-id" scope="col" colSpan={g.cols.length}
                                            className="px-2 py-1 border border-black text-center bg-gray-50 dark:bg-gray-700"
                                            style={isMobile ? undefined : { position: 'sticky', left: 0, zIndex: 3 }}></th>
                                    );
                                }
                                return (
                                    <th key={'grp-' + g.section} scope="col" colSpan={g.cols.length}
                                        className="px-2 py-1 border border-black text-center"
                                        style={gi > 0 ? { borderLeft: SECTION_DIVIDER } : undefined}>
                                        {SECTION_LABELS[g.section]}
                                    </th>
                                );
                            })}
                        </tr>
                        {/* Leaf column header row */}
                        <tr>
                            {visibleColumns.map(c => (
                                <th key={c.key} scope="col"
                                    className="px-2 py-1 border border-black text-center bg-gray-50 dark:bg-gray-700"
                                    style={{
                                        ...freezeStyle(c, 2),
                                        ...(c.bg ? { backgroundColor: c.bg } : {}),
                                        ...(dividerKeys.has(c.key) ? { borderLeft: SECTION_DIVIDER } : {}),
                                    }}>
                                    {c.label}
                                </th>
                            ))}
                        </tr>
                        {/* Inline per-column filter row (contains / = / ≤ / ≥ / between / not between) */}
                        <ColumnFilterRow
                            thClassName="px-1 py-1 border border-black bg-gray-50 dark:bg-gray-700 align-top"
                            cols={visibleColumns.map(c => ({
                                key: c.key,
                                filterable: c.key !== 'select',
                                style: {
                                    ...freezeStyle(c, 2),
                                    ...(dividerKeys.has(c.key) ? { borderLeft: SECTION_DIVIDER } : {}),
                                },
                            }))}
                            filters={filters}
                            onChange={setColFilter} />
                    </thead>
                    <tbody>
                    {visibleRows.map(({ item, index }) => {
                        const rowBg = item.methodName?.id == 1 ? '#bfdbfe' : (item.methodName?.id == 2 ? '#fed7aa' : '#d9f99d');
                        return (
                        <tr key={index} style={{ backgroundColor: rowBg }} className="border border-black-b">
                            {visibleColumns.map(c => (
                                <td key={c.key}
                                    className="px-2 py-1 border border-black text-center"
                                    style={{
                                        ...freezeStyle(c, 1, { backgroundColor: rowBg }),
                                        ...(c.bg ? { backgroundColor: c.bg } : {}),
                                        ...(dividerKeys.has(c.key) ? { borderLeft: SECTION_DIVIDER } : {}),
                                    }}>
                                    {c.render(item, index)}
                                </td>
                            ))}
                        </tr>);
                    })}
                    {hasFilters && visibleRows.length === 0 && (
                        <tr><td className="px-2 py-4 text-center text-gray-500" colSpan={visibleColumns.length}>No rows match the filters.</td></tr>
                    )}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Remarks popup, opened by the (+) next to a Menu Item. */}
            {remarksRow && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onClick={() => setRemarksRow(null)}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <div className="px-4 py-3 border-b">
                            <div className="font-bold text-gray-900">Remarks</div>
                            <div className="text-xs text-gray-500">
                                {itemDisplayName(remarksRow)} — {formatDate(remarksRow.date)}
                            </div>
                        </div>
                        {/* Read-only for everyone but the super admin: the popup is also how a long
                            remark is read in full, so it stays reachable -- only the editing does not. */}
                        <div className="p-4">
                            <textarea
                                rows={5}
                                value={remarksText}
                                onChange={(e) => setRemarksText(e.target.value)}
                                readOnly={!isSuperAdmin}
                                placeholder={isSuperAdmin ? "Enter remarks for this menu item" : "No remarks"}
                                className={`w-full p-2 text-sm text-gray-900 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500${isSuperAdmin ? '' : ' bg-gray-50'}`}
                            />
                            {isSuperAdmin && (
                                <p className="mt-1 text-xs text-gray-500">Saving an empty box clears the remark.</p>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 px-4 py-3 border-t">
                            {isSuperAdmin && (
                                <button type="button" onClick={saveRemarks}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">Submit</button>
                            )}
                            <button type="button" onClick={() => setRemarksRow(null)}
                                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600">
                                {isSuperAdmin ? 'Cancel' : 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
  );
}

export default List;
